import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Switch } from '@headlessui/react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/UI/Button';
import { ConfirmModal } from '../../components/UI/ConfirmModal';
import { useToast } from '../../components/UI/ToastProvider';
import { formService } from '../../services/formService';
import type { FormCollectEmail, FormDefaultSettings, FormPurpose, FormSummary } from '../../types/form';
import {
  FORM_ACCESS_TYPE_LABELS,
  FORM_COLLECT_EMAIL_LABELS,
  FORM_PURPOSE_LABELS,
  FORM_STATUS_LABELS,
} from '../../types/form';

function formatDate(value: string): string {
  try {
    return new Date(value).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return value;
  }
}

function statusBadgeClass(status: FormSummary['status']): string {
  if (status === 'Published') {
    return 'bg-green-100 text-green-700';
  }
  if (status === 'Closed') {
    return 'bg-red-100 text-red-700';
  }
  return 'bg-gray-100 text-gray-700';
}

export const FormsPage: React.FC = () => {
  const [forms, setForms] = useState<FormSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [purposeFilter, setPurposeFilter] = useState<'All' | FormPurpose>('All');
  const [isCreating, setIsCreating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<{
    open: boolean;
    form: FormSummary | null;
  }>({ open: false, form: null });
  const [deleteLinkedDocuments, setDeleteLinkedDocuments] = useState(false);
  const [defaultSettings, setDefaultSettings] = useState<FormDefaultSettings | null>(null);
  const [isSavingDefaults, setIsSavingDefaults] = useState(false);
  const [showDefaults, setShowDefaults] = useState(false);
  const { addToast } = useToast();
  const navigate = useNavigate();

  const loadForms = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await formService.getAll();
      setForms(data);
    } catch {
      addToast('Erro ao carregar formulários. Tente novamente.', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [addToast]);

  const loadDefaultSettings = useCallback(async () => {
    try {
      const data = await formService.getDefaultSettings();
      setDefaultSettings(data);
    } catch {
      // painel opcional
    }
  }, []);

  useEffect(() => {
    loadForms();
    loadDefaultSettings();
  }, [loadForms, loadDefaultSettings]);

  const handleSaveDefaults = async (patch: Partial<FormDefaultSettings>) => {
    if (!defaultSettings || isSavingDefaults) {
      return;
    }

    const next = { ...defaultSettings, ...patch };
    setDefaultSettings(next);

    try {
      setIsSavingDefaults(true);
      const updated = await formService.updateDefaultSettings({
        collectEmailByDefault: next.collectEmailByDefault,
        fieldsRequiredByDefault: next.fieldsRequiredByDefault,
        showProgressBarByDefault: next.showProgressBarByDefault,
        allowResponseEditingByDefault: next.allowResponseEditingByDefault,
      });
      setDefaultSettings(updated);
      addToast('Padrões do tenant atualizados.', 'success');
    } catch {
      await loadDefaultSettings();
      addToast('Erro ao salvar padrões do tenant.', 'error');
    } finally {
      setIsSavingDefaults(false);
    }
  };
  const filteredForms = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return forms.filter((form) => {
      if (purposeFilter !== 'All' && (form.purpose || 'General') !== purposeFilter)
      {
        return false;
      }

      if (!term)
      {
        return true;
      }

      return form.title.toLowerCase().includes(term);
    });
  }, [forms, searchTerm, purposeFilter]);

  const handleCreate = async () => {
    try {
      setIsCreating(true);
      const created = await formService.create({
        title: 'Formulário sem título',
      });
      addToast('Formulário criado.', 'success');
      navigate(`/forms/${created.id}`);
    } catch {
      addToast('Erro ao criar formulário.', 'error');
    } finally {
      setIsCreating(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!confirmDelete.form) {
      return;
    }

    try {
      await formService.delete(
        confirmDelete.form.id,
        deleteLinkedDocuments
      );
      setConfirmDelete({ open: false, form: null });
      setDeleteLinkedDocuments(false);
      addToast(
        deleteLinkedDocuments
          ? 'Formulário excluído. Os arquivos vinculados serão removidos em segundo plano.'
          : 'Formulário excluído. Os arquivos na árvore de documentos foram mantidos.',
        'success'
      );
      await loadForms();
    } catch {
      addToast('Erro ao excluir formulário.', 'error');
    }
  };

  return (
    <div className="p-8">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Formulários</h1>
          <p className="mt-1 text-sm text-gray-500">
            Crie, publique e acompanhe respostas dos seus formulários.
          </p>
        </div>
        <Button
          variant="primary"
          onClick={handleCreate}
          disabled={isCreating}
        >
          {isCreating ? 'Criando...' : 'Novo formulário'}
        </Button>
      </div>

      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        <input
          type="search"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Buscar formulários..."
          className="w-full max-w-md rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
        <select
          value={purposeFilter}
          onChange={(e) => setPurposeFilter(e.target.value as 'All' | FormPurpose)}
          className="rounded-md border border-gray-300 px-3 py-2 text-sm"
        >
          <option value="All">Todas as finalidades</option>
          <option value="General">{FORM_PURPOSE_LABELS.General}</option>
          <option value="Support">{FORM_PURPOSE_LABELS.Support}</option>
        </select>
      </div>

      <div className="mb-6 rounded-lg border border-gray-200 bg-white">
        <button
          type="button"
          className="flex w-full items-center justify-between px-4 py-3 text-left"
          onClick={() => setShowDefaults((prev) => !prev)}
        >
          <div>
            <p className="text-sm font-semibold text-gray-900">
              Padrões para novos formulários
            </p>
            <p className="text-xs text-gray-500">
              Aplicados automaticamente ao criar um formulário neste tenant.
            </p>
          </div>
          <span className="text-sm text-gray-500">
            {showDefaults ? 'Ocultar' : 'Mostrar'}
          </span>
        </button>

        {showDefaults && defaultSettings ? (
          <div className="space-y-4 border-t border-gray-100 px-4 py-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm font-medium text-gray-800">
                Coletar e-mail por padrão
              </p>
              <select
                value={defaultSettings.collectEmailByDefault}
                disabled={isSavingDefaults}
                onChange={(e) =>
                  void handleSaveDefaults({
                    collectEmailByDefault: e.target.value as FormCollectEmail,
                  })
                }
                className="rounded-md border border-gray-300 px-3 py-2 text-sm"
              >
                {(Object.keys(FORM_COLLECT_EMAIL_LABELS) as FormCollectEmail[]).map(
                  (value) => (
                    <option key={value} value={value}>
                      {FORM_COLLECT_EMAIL_LABELS[value]}
                    </option>
                  )
                )}
              </select>
            </div>

            {(
              [
                ['fieldsRequiredByDefault', 'Perguntas obrigatórias por padrão'],
                ['showProgressBarByDefault', 'Barra de progresso por padrão'],
                [
                  'allowResponseEditingByDefault',
                  'Permitir edição de respostas por padrão',
                ],
              ] as const
            ).map(([key, label]) => (
              <div
                key={key}
                className="flex items-center justify-between gap-4 rounded-lg border border-gray-200 px-4 py-3"
              >
                <p className="text-sm font-medium text-gray-800">{label}</p>
                <Switch
                  checked={Boolean(defaultSettings[key])}
                  disabled={isSavingDefaults}
                  onChange={(checked) =>
                    void handleSaveDefaults({ [key]: checked })
                  }
                  className={`${
                    defaultSettings[key] ? 'bg-green-600' : 'bg-gray-200'
                  } relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50`}
                >
                  <span
                    className={`${
                      defaultSettings[key] ? 'translate-x-6' : 'translate-x-1'
                    } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                  />
                </Switch>
              </div>
            ))}
          </div>
        ) : null}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16 text-gray-500">
          Carregando formulários...
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg bg-white shadow">
          <table className="min-w-full">
            <thead>
              <tr className="border-b bg-gray-50 text-left text-sm text-gray-600">
                <th className="px-4 py-3 font-medium">Título</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Finalidade</th>
                <th className="px-4 py-3 font-medium">Acesso</th>
                <th className="px-4 py-3 font-medium">Respostas</th>
                <th className="px-4 py-3 font-medium">Atualizado</th>
                <th className="px-4 py-3 font-medium">Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredForms.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    className="px-4 py-8 text-center text-gray-500"
                  >
                    Nenhum formulário encontrado.
                  </td>
                </tr>
              )}
              {filteredForms.map((form) => (
                <tr key={form.id} className="border-b last:border-b-0">
                  <td className="px-4 py-3 text-gray-900">{form.title}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`rounded px-2 py-1 text-xs font-semibold ${statusBadgeClass(form.status)}`}
                    >
                      {FORM_STATUS_LABELS[form.status]}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`rounded px-2 py-1 text-xs font-semibold ${
                        (form.purpose || 'General') === 'Support'
                          ? 'bg-indigo-100 text-indigo-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {FORM_PURPOSE_LABELS[form.purpose || 'General']}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-700">
                    {FORM_ACCESS_TYPE_LABELS[form.accessType]}
                  </td>
                  <td className="px-4 py-3 text-gray-700">
                    {form.responseCount}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {formatDate(form.updatedAt)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        title="Editar formulário"
                        onClick={() => navigate(`/forms/${form.id}`)}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                          />
                        </svg>
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        title="Excluir formulário"
                        onClick={() => {
                          setDeleteLinkedDocuments(false);
                          setConfirmDelete({ open: true, form });
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmModal
        isOpen={confirmDelete.open}
        title="Excluir formulário"
        message={`Tem certeza que deseja excluir "${confirmDelete.form?.title ?? ''}"? Esta ação não pode ser desfeita.`}
        confirmText="Excluir"
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setConfirmDelete({ open: false, form: null });
          setDeleteLinkedDocuments(false);
        }}
      >
        <label className="mb-6 flex cursor-pointer items-start gap-3 rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            checked={deleteLinkedDocuments}
            onChange={(e) => setDeleteLinkedDocuments(e.target.checked)}
          />
          <span>
            Remover também os arquivos gerados na árvore de documentos
            (anexos enviados nas respostas e exportações em Excel). Se
            desmarcar, esses arquivos permanecerão disponíveis.
          </span>
        </label>
      </ConfirmModal>
    </div>
  );
};
